/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/
import React from "react";
import ReactDOM from "react-dom/client"; // Use ReactDOM from "react-dom/client" in React 18
import "./index.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { createStore, compose, applyMiddleware, combineReducers } from "redux";
import { thunk } from "redux-thunk";
import { Provider } from "react-redux";
import { darkTheme, lightTheme } from "./AppTheme";
import { BrowserRouter } from "react-router-dom";

import reducer from "./store/reducers/auth";
import pageContextReducer from "./store/reducers/page-context";
import { LoaderProvider } from "./context/LoaderContext";
import { ThemeProvider } from "@mui/material";
import { ThemeProviderContext, useThemeContext } from "./utils/ThemeContext";
import AWSIoTConfig from "./utils/pubsub";
import Amplify, { PubSub } from "aws-amplify";
import { AWSIoTProvider } from "@aws-amplify/pubsub";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const composeEnhances = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const saveState = (state) => {
  try {
    // localStorage.setItem("cst", serializedState);
  } catch (e) {
    // Ignore write errors;
  }
};

const rootReducer = combineReducers({
  auth: reducer,
  pageContext: pageContextReducer,
});

const store = createStore(rootReducer, composeEnhances(applyMiddleware(thunk)));
store.subscribe(() => {
  saveState(store.getState());
});



// PubSub (IoT telemetry / terminals) still runs on Amplify. Auth does not — the
// session comes from accounts.vecros.com via SessionSyncFrame (see src/lib/session.js),
// so no Cognito session probe here.
const configureAWS = () => {
  try {
    Amplify.configure(AWSIoTConfig);

    PubSub.addPluggable(
      new AWSIoTProvider({
        aws_pubsub_region: AWSIoTConfig.aws_pubsub_region,
        aws_pubsub_endpoint: AWSIoTConfig.aws_pubsub_endpoint,
      })
    );
  } catch (error) {
    console.error("AWS Configuration failed:", error);
  }
};

configureAWS();

const AppWrapper = () => {
  const { themeMode } = useThemeContext();
  const currentTheme = themeMode ? darkTheme : lightTheme;

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ThemeProvider theme={currentTheme}>
          <div
            style={{
              backgroundColor: currentTheme.palette.background?.default || currentTheme.palette.primary.main,
              minHeight: "100vh",
              width: "100%",
            }}
          >
            <LoaderProvider>
              <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <App />
              </SnackbarProvider>
            </LoaderProvider>
          </div>
        </ThemeProvider>
      </Provider>
    </QueryClientProvider>
  );
};

export default AppWrapper;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ThemeProviderContext>
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  </ThemeProviderContext>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
serviceWorker.unregister();
